/**
 * Frontend-speil av datologikken i functions/lib/articleWheel.js.
 *
 * Duplisert, ikke importert: frontend og functions har hver sin build og
 * tsconfig, så `functions/`-pakken kan ikke importeres herfra. Hold de to filene
 * i sync ved endringer — enhetstestene i test/articleWheel.test.ts dekker
 * backend-versjonen, som er fasiten.
 *
 * Ingen I/O — ren dato-/kalenderregning, trygt å bruke direkte i render.
 */
import type { WheelRecurrence, ArticleWheelEntry } from '../types';

/** Påskesøndag (gregoriansk «anonymous»-algoritme). */
export function computeEaster(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  return {
    month: Math.floor((h + l - 7 * m + 114) / 31),
    day: ((h + l - 7 * m + 114) % 31) + 1,
  };
}

const utc = (year: number, month1to12: number, day: number) => new Date(Date.UTC(year, month1to12 - 1, day));
const addDays = (date: Date, days: number) => new Date(date.getTime() + days * 86400000);

/** «YYYY-MM-DD» fra en UTC-dato. */
export function ymdUTC(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function nthWeekdayOfMonth(year: number, month1to12: number, weekday: number, n: number): Date {
  if (n === -1) {
    const last = new Date(Date.UTC(year, month1to12, 0));
    return addDays(last, -((last.getUTCDay() - weekday + 7) % 7));
  }
  const first = utc(year, month1to12, 1);
  return addDays(first, ((weekday - first.getUTCDay() + 7) % 7) + (n - 1) * 7);
}

/** Konkret dato for en anledning i et gitt år. */
export function occasionDate(rec: WheelRecurrence, year: number): Date {
  if (!rec || typeof rec !== 'object') return utc(year, 1, 1);
  if (rec.type === 'fixed') return utc(year, rec.month, rec.day);
  if (rec.type === 'nthWeekday') {
    return addDays(nthWeekdayOfMonth(year, rec.month, rec.weekday, rec.n), rec.offsetDays || 0);
  }
  if (rec.type === 'easterOffset') {
    const e = computeEaster(year);
    return addDays(utc(year, e.month, e.day), rec.offset);
  }
  return utc(year, 1, 1);
}

/** Produksjonsdato = anledning minus leadDays. */
export function produceDate(entry: Pick<ArticleWheelEntry, 'recurrence' | 'leadDays'>, year: number): Date {
  return addDays(occasionDate(entry.recurrence, year), -Math.max(0, entry.leadDays || 0));
}

/** Dagens dato i Oslo som «YYYY-MM-DD» — samme kilde som cronen bruker. */
export function todayYmd(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Oslo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Neste forekomst (anledningsdato) fra og med en gitt dag. */
export function nextOccurrenceYmd(rec: WheelRecurrence, fromYmd: string): string {
  const fromYear = Number(fromYmd.slice(0, 4));
  for (const year of [fromYear, fromYear + 1]) {
    const occ = ymdUTC(occasionDate(rec, year));
    if (occ >= fromYmd) return occ;
  }
  return ymdUTC(occasionDate(rec, fromYear + 1));
}

/** Antall hele dager fra `fromYmd` til `toYmd`. Negativ hvis passert. */
export function daysBetweenYmd(fromYmd: string, toYmd: string): number {
  return Math.round((Date.parse(`${toYmd}T00:00:00Z`) - Date.parse(`${fromYmd}T00:00:00Z`)) / 86400000);
}

/** Måneder (brøk) mellom to datoer — 30-dagers tilnærming, nok for en runway-visning. */
export function monthsBetweenYmd(fromYmd: string, toYmd: string): number {
  return daysBetweenYmd(fromYmd, toYmd) / 30;
}

/** «Content runway»: hvor mange måneder planlagt innhold som gjenstår. */
export function computeRunwayMonths(planHorizonYmd: string | undefined | null, from: string): number {
  if (!planHorizonYmd) return 0;
  return Math.max(0, monthsBetweenYmd(from, planHorizonYmd));
}

export const NORSKE_MANEDER = [
  'januar', 'februar', 'mars', 'april', 'mai', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'desember',
];

const UKEDAGER = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
const ORDENSTALL = ['', 'første', 'andre', 'tredje', 'fjerde', 'femte'];

/** «17. mai 2026» fra «2026-05-17». */
export function formatWheelDate(ymd: string): string {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return `${d}. ${NORSKE_MANEDER[m - 1]} ${y}`;
}

/** «om 12 dager» / «i dag» / «i morgen» — relativ etikett for kommende merkedag. */
export function formatRelativeDays(days: number): string {
  if (days === 0) return 'i dag';
  if (days === 1) return 'i morgen';
  if (days < 0) return `for ${Math.abs(days)} dag${Math.abs(days) === 1 ? '' : 'er'} siden`;
  return `om ${days} dager`;
}

/** Menneskelig beskrivelse av en gjentakelse — brukes når `when` ikke er satt. */
export function describeRecurrence(rec: WheelRecurrence): string {
  if (!rec || typeof rec !== 'object') return '—';
  if (rec.type === 'fixed') return `${rec.day}. ${NORSKE_MANEDER[rec.month - 1] ?? ''}`.trim();
  if (rec.type === 'nthWeekday') {
    const base = rec.n === -1
      ? `siste ${UKEDAGER[rec.weekday]} i ${NORSKE_MANEDER[rec.month - 1]}`
      : `${ORDENSTALL[rec.n] || `${rec.n}.`} ${UKEDAGER[rec.weekday]} i ${NORSKE_MANEDER[rec.month - 1]}`;
    if (!rec.offsetDays) return base;
    const n = Math.abs(rec.offsetDays);
    return `${n} dag${n === 1 ? '' : 'er'} ${rec.offsetDays > 0 ? 'etter' : 'før'} ${base}`;
  }
  if (rec.offset === 0) return 'påskesøndag';
  const n = Math.abs(rec.offset);
  return `${n} dag${n === 1 ? '' : 'er'} ${rec.offset > 0 ? 'etter' : 'før'} påske`;
}

export interface WheelEntryView {
  entry: ArticleWheelEntry;
  /** Neste anledningsdato. */
  nextYmd: string;
  /** Dagen artikkelen produseres (anledning minus leadDays). */
  produceYmd: string;
  /** Dager til neste anledning. */
  daysUntil: number;
  /** Er produksjonsvinduet åpent nå? */
  windowOpen: boolean;
}

/**
 * Oppføringer sortert etter neste forekomst, beriket for visning.
 * Deaktiverte oppføringer beholdes — de skal vises, bare nedtonet.
 */
export function buildWheelView(entries: ArticleWheelEntry[], from: string): WheelEntryView[] {
  return (entries || [])
    .map((entry) => {
      const nextYmd = nextOccurrenceYmd(entry.recurrence, from);
      const year = Number(nextYmd.slice(0, 4));
      const produceYmd = ymdUTC(produceDate(entry, year));
      return {
        entry,
        nextYmd,
        produceYmd,
        daysUntil: daysBetweenYmd(from, nextYmd),
        windowOpen: entry.enabled && produceYmd <= from && from <= nextYmd && entry.lastProducedYear !== year,
      };
    })
    .sort((a, b) => a.nextYmd.localeCompare(b.nextYmd));
}
